//pltfrm-landing/src/main.jsx
import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import AppErrorBoundary from "./components/AppErrorBoundary.jsx";
import {
  consumeSupportAdminHandoff,
  consumeSupportStudentHandoff,
} from "./utils/authSession.js";

import RouteChangeLoader from "./components/RouteChangeLoader";
import PublicLocaleRoute from "./public/PublicLocaleRoute.jsx";
import { PUBLIC_LOCALE_CODES } from "./public/publicLocale.js";


import "@fontsource-variable/nunito-sans/wght.css";
import "./ui/theme/tokens.css";
import "./ui/typography/fonts.css";
import "./index.css";
import "./i18n/i18n.js";

const TuitoriaLanding = lazy(() => import("./tuitoria_landing/TuitoriaLanding.jsx"));
const AboutApp = lazy(() => import("./AboutApp.jsx"));
const NotFound = lazy(() => import("./pages/NotFound.jsx"));
const AdminPage = lazy(() => import("./pages/AdminPage.jsx"));
const VideoLessonStudioPage = lazy(() => import("./pages/admin/VideoLessonStudioPage.jsx"));
const CertificateStudioPage = lazy(() => import("./pages/admin/CertificateStudioPage.jsx"));
const PublicCertificatePage = lazy(() => import("./certificate_studio/verification/PublicCertificatePage.jsx"));
const StudentCertificateRequestPage = lazy(() => import("./certificate_studio/student_request/StudentCertificateRequestPage.jsx"));
const CabinetPage = lazy(() => import("./pages/CabinetPage.jsx"));
const TariffsPage = lazy(() => import("./pages/TariffsPage.jsx"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage.jsx"));
const TermsPage = lazy(() => import("./pages/TermsPage.jsx"));
const CookiesPage = lazy(() => import("./pages/CookiesPage.jsx"));
const PostPaymentPage = lazy(() => import("./pages/PostPaymentPage.jsx"));
const PaymentFailedPage = lazy(() => import("./pages/PaymentFailedPage.jsx"));
const PlatformCreatedPage = lazy(() => import("./pages/PlatformCreatedPage.jsx"));
const AboutPlatformPage = lazy(() => import("./pages/AboutPlatformPage.jsx"));
const TutorPlatformPage = lazy(() => import("./pages/TutorPlatformPage.jsx"));
const OnlineSchoolPlatformPage = lazy(() =>
  import("./pages/OnlineSchoolPlatformPage.jsx")
);
const BlogPage = lazy(() => import("./pages/BlogPage.jsx"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage.jsx"));
const KnowledgeBasePage = lazy(() => import("./pages/KnowledgeBasePage"));
const KbTeachersPage = lazy(() => import("./pages/KbTeachersPage"));
const KbStudentsPage = lazy(() => import("./pages/KbStudentsPage"));
const KbGettingStartedPage = lazy(() => import("./pages/KbGettingStartedPage"));
const KbBillingPage = lazy(() => import("./pages/KbBillingPage"));
const KbTechPage = lazy(() => import("./pages/KbTechPage"));
const PreviewCoursePage = lazy(() => import("./pages/PreviewCoursePage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage.jsx"));
const StatusPage = lazy(() => import("./pages/StatusPage.jsx"));
const CreatePlatformPage = lazy(() => import("./pages/CreatePlatformPage.jsx"));
const LandingEliana = lazy(() => import("./landing_eliana/Landing.jsx"));
const MyMessagesPage = lazy(() => import("./pages/MyMessagesPage"));
const AuthPage = lazy(() => import("./pages/AuthPage.jsx"));
const PasswordRecoveryPage = lazy(() => import("./pages/PasswordRecoveryPage.jsx"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage.jsx"));
const VerifyStudentEmailPage = lazy(() => import("./pages/VerifyStudentEmailPage.jsx"));
const AcceptStudentInvitationPage = lazy(() => import("./pages/AcceptStudentInvitationPage.jsx"));
const StudentCourseCheckoutPage = lazy(() => import("./pages/StudentCourseCheckoutPage.jsx"));
const PublicProductPage = lazy(() => import("./pages/PublicProductPage.jsx"));
const PublicCourseLanding = lazy(() => import("./landing_builder/pages/PublicCourseLanding.jsx"));
const PaySuccessPage = lazy(() => import("./pages/PaySuccessPage.jsx"));
const ChangelogPage = lazy(() => import("./pages/ChangelogPage.jsx"));
const IosInstallPage = lazy(() => import("./pages/IosInstallPage"));
const PlatformHomePage = lazy(() => import("./pages/PlatformHomePage"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const CommunitySsoPage = lazy(() => import("./pages/CommunitySsoPage.jsx"));
const CommunityProfilePage = lazy(() => import("./pages/community/CommunityProfilePage.jsx"));
const PaidCourseSmokeLanding = lazy(() => import("./landing_builder/templates/paid_course_smoke/PaidCourseSmokeLanding.jsx"));

consumeSupportAdminHandoff();
consumeSupportStudentHandoff();

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <BrowserRouter>
        <RouteChangeLoader delayMs={200} minShowMs={180}>
          <Suspense fallback={null}>
            <Routes>
            <Route path="/" element={<TuitoriaLanding />} />

            {PUBLIC_LOCALE_CODES.map((locale) => (
              <Route
                key={`public-${locale}`}
                path={`/${locale}`}
                element={<PublicLocaleRoute locale={locale} />}
              >
                <Route index element={<TuitoriaLanding />} />

                <Route path="pricing" element={<TariffsPage />} />
                <Route path="about" element={<AboutPlatformPage />} />

                {locale === "uk" && (
                  <Route path="repetytor" element={<TutorPlatformPage />} />
                )}

                {locale === "uk" && (
                  <Route
                    path="platforma-dlya-onlain-shkoly"
                    element={<OnlineSchoolPlatformPage />}
                  />
                )}

                <Route
                  path="blog"
                  element={
                    <Navigate
                      to={`/${locale}/articles`}
                      replace
                    />
                  }
                />
                <Route path="blog/:slug" element={<BlogArticlePage />} />
                <Route path="articles" element={<BlogPage />} />
                <Route path="articles/:slug" element={<BlogArticlePage />} />

                <Route path="features" element={<FeaturesPage />} />

                <Route
                  path="knowledge-base"
                  element={<KnowledgeBasePage />}
                />
                <Route
                  path="knowledge-base/teachers"
                  element={<KbTeachersPage />}
                />
                <Route
                  path="knowledge-base/students"
                  element={<KbStudentsPage />}
                />
                <Route
                  path="knowledge-base/getting-started"
                  element={<KbGettingStartedPage />}
                />
                <Route
                  path="knowledge-base/billing"
                  element={<KbBillingPage />}
                />
                <Route
                  path="knowledge-base/tech"
                  element={<KbTechPage />}
                />

                <Route path="changelog" element={<ChangelogPage />} />
                <Route path="privacy" element={<PrivacyPage />} />
                <Route path="terms" element={<TermsPage />} />
                <Route path="cookies" element={<CookiesPage />} />
              </Route>
            ))}

            <Route path="/tuitoria-preview" element={<TuitoriaLanding />} />
            <Route path="/landing-eliana" element={<LandingEliana />} />
            <Route path="/:slug/landing2" element={<LandingEliana />} />

            <Route path="/login" element={<AuthPage />} />
            <Route
              path="/forgot-password"
              element={<PasswordRecoveryPage mode="request" />}
            />
            <Route
              path="/reset-password"
              element={<PasswordRecoveryPage mode="reset" />}
            />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route
              path="/:slug/verify-student-email"
              element={<VerifyStudentEmailPage />}
            />
            <Route
              path="/:slug/accept-student-invitation"
              element={<AcceptStudentInvitationPage />}
            />
            <Route
              path="/verify/c/demo-certificate"
              element={<PublicCertificatePage />}
            />
            <Route
              path="/certificate/request/demo"
              element={<StudentCertificateRequestPage />}
            />
            <Route path="/:slug/login" element={<AuthPage />} />

            <Route path="/create-platform" element={<CreatePlatformPage />} />
            <Route path="/pricing" element={<TariffsPage />} />
            <Route path="/pay/success" element={<PaySuccessPage />} />

            <Route path="/post-payment" element={<PostPaymentPage />} />
            <Route path="/payment-failed" element={<PaymentFailedPage />} />
            <Route path="/platform-created" element={<PlatformCreatedPage />} />
            <Route path="/about" element={<AboutPlatformPage />} />

            <Route path="/blog" element={<Navigate to="/articles" replace />} />
            <Route path="/blog/:slug" element={<BlogArticlePage />} />
            <Route path="/articles" element={<BlogPage />} />
            <Route path="/articles/:slug" element={<BlogArticlePage />} />

            <Route path="/features" element={<FeaturesPage />} />

            <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
            <Route
              path="/knowledge-base/teachers"
              element={<KbTeachersPage />}
            />
            <Route
              path="/knowledge-base/students"
              element={<KbStudentsPage />}
            />
            <Route
              path="/knowledge-base/getting-started"
              element={<KbGettingStartedPage />}
            />
            <Route path="/knowledge-base/billing" element={<KbBillingPage />} />
            <Route path="/knowledge-base/tech" element={<KbTechPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route
              path="/community/threads/:threadId"
              element={<CommunityPage />}
            />
            <Route
              path="/community/profile"
              element={<CommunityProfilePage />}
            />
            <Route path="/community-sso" element={<CommunitySsoPage />} />

            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/:slug/changelog" element={<ChangelogPage />} />

            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookies" element={<CookiesPage />} />

            <Route path="/admin" element={<AdminPage />} />
            <Route path="/profile" element={<CabinetPage />} />

            <Route path="/:slug/admin" element={<AdminPage />} />
            <Route path="/:slug/student" element={<CabinetPage />} />
            <Route path="/:slug/profile" element={<CabinetPage />} />
            <Route
              path="/:slug/courses/:courseId/checkout"
              element={<StudentCourseCheckoutPage />}
            />
            <Route
              path="/:platformSlug/p/:productId"
              element={<PublicProductPage />}
            />
            <Route
              path="/:slug/landing-test"
              element={<PaidCourseSmokeLanding />}
            />
            <Route
              path="/:slug/payment-failed"
              element={<PaymentFailedPage />}
            />

            <Route
              path="/:platformSlug/preview"
              element={<PreviewCoursePage mode="preview" />}
            />
            <Route
              path="/ru/:platformSlug/preview"
              element={<PreviewCoursePage mode="preview" />}
            />
            <Route
              path="/:platformSlug/course/:courseSlug"
              element={<PublicCourseLanding />}
            />

            <Route path="/:platformSlug/home" element={<PlatformHomePage />} />
            <Route
              path="/ru/:platformSlug/home"
              element={<PlatformHomePage />}
            />

            <Route path="/about-app" element={<AboutApp />} />
            <Route path="/status" element={<StatusPage />} />

            <Route path="/messages" element={<MyMessagesPage />} />
            <Route path="/:slug/messages" element={<MyMessagesPage />} />

            <Route path="/install-ios" element={<IosInstallPage />} />
            <Route path="/videostudio" element={<VideoLessonStudioPage />} />
            <Route path="/videostudio/" element={<VideoLessonStudioPage />} />
            <Route
              path="/:slug/certificate-studio"
              element={<CertificateStudioPage />}
            />

            <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </RouteChangeLoader>
      </BrowserRouter>
    </AppErrorBoundary>
  </React.StrictMode>,
);
